import { Box, Collapse, Fade, Grid, IconButton, Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { MeterInput, NewMeter } from '@vy/types';
import { AggregationWithFieldOperatorSchema } from '@vy/types';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useToggle } from '../../../../hooks/use-toggle';
import { CreditTypesMultiSelect } from '../../shared/CreditTypesMultiSelect';
import { AggregationSelect } from './AggregationSelect';
import { MeterFieldSelect } from './MeterFieldSelect';
import { SectionContainer } from './SectionContainer';

export const CreateProductPageSummary: React.FC = () => {
  const { control } = useFormContext<MeterInput, unknown, NewMeter>();
  const [collapseAdvanced, toggleCollapseAdvanced] = useToggle(false);

  const eventName = useWatch({
    control,
    name: 'eventName',
  });
  const aggregationOperator = useWatch({
    control,
    name: 'aggregationMethod.operator',
  });

  const isParameterizedAggregationMethod = checkIsParameterizedAggregationMethod(aggregationOperator);

  return (
    <SectionContainer title="Summary">
      <Grid
        container
        direction="row"
        sx={{
          alignItems: "end",
          columnGap: 1.5
        }}>
        <Grid>
          <Box>
            <Typography noWrap sx={{
              marginBottom: "8px"
            }}>
              Sum up
            </Typography>
          </Box>
        </Grid>

        <Grid sx={{
          flex: 1
        }}>
          <Controller
            control={control}
            name="aggregationMethod.operator"
            // defaultValue="Count"
            render={({ field }) => (
              <AggregationSelect
                value={field.value}
                onChange={(event, newValue) => {
                  event.preventDefault();
                  field.onChange(newValue);
                }}
                inputRef={field.ref}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid sx={{
          flex: 1
        }}>
          <Fade in={isParameterizedAggregationMethod}>
            <Box
              sx={{
                columnGap: '12px',
                display: 'flex',
                alignItems: 'end',
              }}
            >
              <Typography sx={{
                marginBottom: "8px"
              }}>on</Typography>

              <Controller
                control={control}
                name="aggregationMethod.field"
                render={({ field }) => (
                  <MeterFieldSelect
                    eventName={eventName}
                    value={field.value}
                    onChange={(event, newValue) => {
                      event.preventDefault();
                      field.onChange(newValue);
                    }}
                    inputRef={field.ref}
                    fullWidth
                  />
                )}
              />
            </Box>
          </Fade>
        </Grid>

        <Grid>
          <Box>
            <IconButton
              size="small"
              sx={(theme) => ({
                color: collapseAdvanced ? theme.palette.primary.main : theme.palette.text.secondary,
                columnGap: '8px',
                marginBottom: '4px',
              })}
              onClick={() => toggleCollapseAdvanced()}
            >
              <VyIcon icon="Settings" />
            </IconButton>
          </Box>
        </Grid>

        <Grid container size={12}>
          <Collapse in={collapseAdvanced} sx={{ width: '100%' }}>
            <Stack sx={{ marginTop: 2, rowGap: 2, width: '100%' }}>
              <Controller
                control={control}
                name="aggregationMethod.instanceKey"
                render={({ field }) => (
                  <MeterFieldSelect
                    eventName={eventName}
                    value={field.value}
                    onChange={(event, newValue) => {
                      event.preventDefault();
                      field.onChange(newValue);
                    }}
                    label="Group by"
                    sx={{
                      width: '240px',
                    }}
                    inputRef={field.ref}
                  />
                )}
              />

              <Controller
                control={control}
                name="creditTypeIds"
                defaultValue={[]}
                render={({ field }) => (
                  <CreditTypesMultiSelect
                    value={field.value ?? []}
                    onChange={field.onChange}
                    dataVy="create-product-page-credit-types-select"
                  />
                )}
              />
            </Stack>
          </Collapse>
        </Grid>
      </Grid>
    </SectionContainer>
  );
};

function checkIsParameterizedAggregationMethod(aggregationOperator: string | undefined) {
  return AggregationWithFieldOperatorSchema.safeParse(aggregationOperator).success;
}
